// ----------------------------------------------------------------------
// The To-Go dining table count control: what a new count does to the
// restaurant's table codes (Table 1..n), worded for the confirm under the
// Generate / Update / Regenerate button. restaurant-service applies the same
// rules. Ported from admin-app (src/components/restaurant-detail/togoTables.ts).
// ----------------------------------------------------------------------

export const MAX_TOGO_TABLES = 100;

export type ParsedCount = { ok: true; value: number } | { ok: false };

/** A typed count: digits only, 1..MAX_TOGO_TABLES. */
export function parseCount(text: string): ParsedCount {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return { ok: false };
  const value = Number(trimmed);
  return value >= 1 && value <= MAX_TOGO_TABLES ? { ok: true, value } : { ok: false };
}

/** What the count button reads: nothing yet → Generate, a new n → Update, same n → Regenerate. */
export function countAction(current: number, next: number): 'Generate' | 'Update' | 'Regenerate' {
  if (current === 0) return 'Generate';
  return next === current ? 'Regenerate' : 'Update';
}

export interface CountChange {
  kind: 'add' | 'remove' | 'same';
  title: string;
  body: string;
  chips: { label: string; tone: 'keep' | 'add' | 'remove' }[];
  confirm: string;
}

const span = (a: number, b: number) => (a === b ? `Table ${a}` : `Table ${a} – Table ${b}`);
const tables = (n: number) => `${n} ${n === 1 ? 'table' : 'tables'}`;

/** What going from ``current`` codes to ``next`` does. */
export function countChange(current: number, next: number): CountChange {
  if (next > current) {
    return {
      kind: 'add',
      title: current ? `Add ${tables(next - current)}?` : `Generate ${tables(next)}?`,
      body: current
        ? `Tables 1 to ${current} keep their codes, so printed cards keep working. New codes are made for the added tables.`
        : `Codes are made for ${span(1, next)}.`,
      chips: [
        ...(current ? [{ label: `Keeps ${span(1, current)}`, tone: 'keep' as const }] : []),
        { label: `Adds ${span(current + 1, next)}`, tone: 'add' },
      ],
      confirm: current ? 'Add tables' : `Generate ${next}`,
    };
  }
  if (next < current) {
    return {
      kind: 'remove',
      title: `Remove ${tables(current - next)}?`,
      body: `Cards already printed for the removed tables stop taking orders straight away. Tables 1 to ${next} are unchanged.`,
      chips: [
        { label: `Keeps ${span(1, next)}`, tone: 'keep' },
        { label: `Removes ${span(next + 1, current)}`, tone: 'remove' },
      ],
      confirm: 'Remove tables',
    };
  }
  return {
    kind: 'same',
    title: `Regenerate all ${tables(current)}?`,
    body: 'Same table numbers and links, fresh QR images. Printed cards keep working; reprint only after a POS change, which gives the tables new links.',
    chips: [{ label: span(1, current), tone: 'keep' }],
    confirm: 'Regenerate',
  };
}
